import { AfterViewInit, Component, OnInit } from '@angular/core';
import { ECharts, EChartsOption } from 'echarts';
import { getRevenueMetricsGetter } from 'src/app/components/echarts-dashboard-item/compose-data-helpers';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import {
    DashboardService,
    IDashboardInitOptions,
} from 'src/app/services/dashboard/dashboard.service';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import { DatasourceColumnId } from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { defaultColorGetterBuilder } from 'src/app/utils/echarts/series-color';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { ICheckboxSettingDashboardItem } from '../common/items/setting/checkbox/checkbox.component';
import { IToggleSettingDashboardItem } from '../common/items/setting/toggle/toggle.component';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { CompanyMasterdashboardChartDocs } from './company-masterdashboard.chart-documentation';
import { Activity30DaysMonthlyDataLoaderFactory } from './data-loaders/acitvity-30days-monthly';
import { AdsDailyLast30DaysDataLoaderFactory } from './data-loaders/ads-daily-last-30days';
import { AdsMonthlyLast16MonthsDataLoaderFactory } from './data-loaders/ads-monthly-last-16months';
import { CampaignAttributionKpisDailyDataLoaderFactory } from './data-loaders/campaign-attribution-kpis-daily';
import { CampaignAttributionKpisMonthlyDataLoaderFactory } from './data-loaders/campaign-attribution-kpis-monthly';
import { CohortRevenueDailyAggDataLoaderFactory } from './data-loaders/cohort-revenue-daily-agg';
import { FirstMarginDailyLast30DaysDataLoaderFactory } from './data-loaders/first-margin-daily-last-30days';
import { FirstMarginMonthlyDataLoaderFactory } from './data-loaders/first-margin-monthly';
import { PlayerKpisDailyDataLoaderFactory } from './data-loaders/player-kpis-daily';
import { PlayerKpisMonthlyDataLoaderFactory } from './data-loaders/player-kpis-monthly';
import { RoasDailyD30DataLoaderFactory } from './data-loaders/roas-daily-d30';
import { RoasDailyD7DataLoaderFactory } from './data-loaders/roas-daily-d7';
import { RoasMonthlyD30DataLoaderFactory } from './data-loaders/roas-monthly-d30';
import { RoasMonthlyD7DataLoaderFactory } from './data-loaders/roas-monthly-d7';
import { UsersDaysDailyD30DataLoaderFactory } from './data-loaders/users-days-daily-d30';
import { UsersDaysDailyD7DataLoaderFactory } from './data-loaders/users-days-daily-d7';
import { UsersDaysMonthlyD30DataLoaderFactory } from './data-loaders/users-days-monthly-d30';
import { BiVizDashboardCompanyPlayerKpisDailySqlMap } from './sql-maps/biviz-dashboard-company-player-kpis-daily-sql-map';

@Component({
    selector: 'app-company-masterdasboard',
    templateUrl: './company-masterdashboard.component.html',
    styleUrls: ['./company-masterdashboard.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class CompanyMasterdashboardComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Company Masterdashboard';
    override DASHBOARD_ID = 'company-masterdashboard';

    override dashboardInitOptions: IDashboardInitOptions = {
        fullscreenClassName: 'company-main-content',
    };

    values = Object.values;

    private gcpProjectId: GcpProjectId = currentDataProject().gcpProjectId;

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why they are created here and referenced later
    ////
    /////////////////////////////////////////////////////////////////

    /////////////////////////////////////////////////////////////////
    ////
    ////    CHART DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    override chartDataLoaders = {
        'company.masterdashboard.activity_30days_monthly':
            new Activity30DaysMonthlyDataLoaderFactory({
                createdInstanceId:
                    'company.masterdashboard.activity_30days_monthly',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'company.masterdashboard.ads_daily_last_30days':
            new AdsDailyLast30DaysDataLoaderFactory({
                createdInstanceId:
                    'company.masterdashboard.ads_daily_last_30days',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'company.masterdashboard.ads_monthly_last_16months':
            new AdsMonthlyLast16MonthsDataLoaderFactory({
                createdInstanceId:
                    'company.masterdashboard.ads_monthly_last_16months',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'company.masterdashboard.campaign_attribution_kpis_daily':
            new CampaignAttributionKpisDailyDataLoaderFactory({
                createdInstanceId:
                    'company.masterdashboard.campaign_attribution_kpis_daily',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'company.masterdashboard.campaign_attribution_kpis_monthly':
            new CampaignAttributionKpisMonthlyDataLoaderFactory({
                createdInstanceId:
                    'company.masterdashboard.campaign_attribution_kpis_monthly',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'company.masterdashboard.cohort_revenue_daily_agg':
            new CohortRevenueDailyAggDataLoaderFactory({
                createdInstanceId:
                    'company.masterdashboard.cohort_revenue_daily_agg',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'company.masterdashboard.first_margin_daily_last_30days':
            new FirstMarginDailyLast30DaysDataLoaderFactory({
                createdInstanceId:
                    'company.masterdashboard.first_margin_daily_last_30days',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'company.masterdashboard.first_margin_monthly':
            new FirstMarginMonthlyDataLoaderFactory({
                createdInstanceId:
                    'company.masterdashboard.first_margin_monthly',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'company.masterdashboard.player_kpis_daily':
            new PlayerKpisDailyDataLoaderFactory({
                createdInstanceId: 'company.masterdashboard.player_kpis_daily',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'company.masterdashboard.player_kpis_monthly':
            new PlayerKpisMonthlyDataLoaderFactory({
                createdInstanceId:
                    'company.masterdashboard.player_kpis_monthly',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'company.masterdashboard.roas_daily_d7':
            new RoasDailyD7DataLoaderFactory({
                createdInstanceId: 'company.masterdashboard.roas_daily_d7',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'company.masterdashboard.roas_daily_d30':
            new RoasDailyD30DataLoaderFactory({
                createdInstanceId: 'company.masterdashboard.roas_daily_d30',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'company.masterdashboard.roas_monthly_d7':
            new RoasMonthlyD7DataLoaderFactory({
                createdInstanceId: 'company.masterdashboard.roas_monthly_d7',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'company.masterdashboard.roas_monthly_d30':
            new RoasMonthlyD30DataLoaderFactory({
                createdInstanceId: 'company.masterdashboard.roas_monthly_d30',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'company.masterdashboard.users_days_daily_d7':
            new UsersDaysDailyD7DataLoaderFactory({
                createdInstanceId:
                    'company.masterdashboard.users_days_daily_d7',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'company.masterdashboard.users_days_daily_d30':
            new UsersDaysDailyD30DataLoaderFactory({
                createdInstanceId:
                    'company.masterdashboard.users_days_daily_d30',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),

        'company.masterdashboard.users_days_monthly_d30':
            new UsersDaysMonthlyD30DataLoaderFactory({
                createdInstanceId:
                    'company.masterdashboard.users_days_monthly_d30',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
            }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    FILTER DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    override filterDataLoaders = {
        // 'company.masterdashboard.filter.revenue_type': DataLoaderFactories[
        //     'common.filter.datasource_column'
        // ]({
        //     createdInstanceId: 'company.masterdashboard.filter.revenue_type',
        //     gcpProjectId: this.gcpProjectId,
        //     queryCacheService: this.queryCache,
        //     datasourceColumnId: 'REVENUE_TYPE',
        //     datasourceSqlMap: BiVizDashboardCompanyPlayerKpisDailySqlMap.sqlMap,
        // }).create(),
        'company.masterdashboard.filter.project_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'company.masterdashboard.filter.project_name',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'COMPANY_PROJECT_NAME',
            datasourceSqlMap: BiVizDashboardCompanyPlayerKpisDailySqlMap.sqlMap,
        }).create(),
        'company.masterdashboard.filter.firebase_app_store':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'company.masterdashboard.filter.firebase_firebase_app_store',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_APP_STORE',
                datasourceSqlMap:
                    BiVizDashboardCompanyPlayerKpisDailySqlMap.sqlMap,
            }).create(),
        'company.masterdashboard.filter.firebase_country_tier':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'company.masterdashboard.filter.firebase_country_tier',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
                datasourceSqlMap:
                    BiVizDashboardCompanyPlayerKpisDailySqlMap.sqlMap,
            }).create(),
        'company.masterdashboard.filter.firebase_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'company.masterdashboard.filter.firebase_country',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY',
            datasourceSqlMap: BiVizDashboardCompanyPlayerKpisDailySqlMap.sqlMap,
        }).create(),
        'company.masterdashboard.filter.etl_payer': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'company.masterdashboard.filter.etl_payer',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ETL_PAYER',
            datasourceSqlMap: BiVizDashboardCompanyPlayerKpisDailySqlMap.sqlMap,
        }).create(),
    };
    /////////////////////////////////////////////////////////////////
    ////
    ////    DASHBOARD ITEMS
    ////
    /////////////////////////////////////////////////////////////////

    /////////////////////////////////////////////////////////////////
    ////
    ////    SETTINGS ITEMS
    ////
    /////////////////////////////////////////////////////////////////

    settingItemNetGross: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.NET_GROSS,
            viewtype: 'toggle',
            value: 'net',
            selectOptions: ['net', 'gross'],
        },
    };

    settingItemCurrency: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.CURRENCY,
            viewtype: 'toggle',
            value: 'pln',
            selectOptions: ['pln', 'usd'],
        },
    };
    settingItemShowTotalRevenue: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_TOTAL_REVENUE,
            viewtype: 'checkbox',
            displayName: 'total',
            value: true,
        },
    };
    settingItemShowInappRevenue: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_INAPP_REVENUE,
            viewtype: 'checkbox',
            displayName: 'inapp',
        },
    };
    settingItemShowAdRevenue: ICheckboxSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SHOW_AD_REVENUE,
            viewtype: 'checkbox',
            displayName: 'ad',
        },
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    CHARTS ITEMS
    ////
    /////////////////////////////////////////////////////////////////

    avg_user_lifetime_d7: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.avg_user_lifetime_d7',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId:
                'company.masterdashboard.chart.avg_user_lifetime_d7',
            dashboardItem: {
                title: 'Average user lifetime D7',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.avg_user_lifetime_d7,
                // subtitle: 'Ignored filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_user_lifetime_d7',
            metricName: 'Avg user lifetime D7',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `First open date: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter: (v: number) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                v,
                                ' days'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['company.masterdashboard.users_days_daily_d7']
                .emitter,
    };

    avg_user_lifetime_d30: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.avg_user_lifetime_d30',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId:
                'company.masterdashboard.chart.avg_user_lifetime_d30',
            dashboardItem: {
                title: 'Average user lifetime D30',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.avg_user_lifetime_d30,
                // subtitle: 'Ignored filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_user_lifetime_d30',
            metricName: 'Avg user lifetime D30',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `First open date: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter: (v: number) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                v,
                                ' days'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'company.masterdashboard.users_days_daily_d30'
            ].emitter,
    };
    avg_user_lifetime_d30_monthly: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.avg_user_lifetime_d30_monthly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId:
                'company.masterdashboard.chart.avg_user_lifetime_d30_monthly',
            dashboardItem: {
                title: 'Average user lifetime D30 Monthly',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.avg_user_lifetime_d30_monthly,
                // subtitle: 'Ignored filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_user_lifetime_d30_monthly',
            metricName: 'Monthly avg user lifetime D30 ',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter: (v: number) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                v,
                                ' days'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'company.masterdashboard.users_days_monthly_d30'
            ].emitter,
    };
    dau: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.dau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId: 'company.masterdashboard.chart.dau',
            dashboardItem: {
                title: 'DAU',
                chartDocumentation: CompanyMasterdashboardChartDocs.dau,
                // subtitle: 'Unsupported filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'DAU',
            metricName: 'DAU',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['company.masterdashboard.player_kpis_daily']
                .emitter,
    };
    stickiness_dau_mau: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.stickiness_dau_mau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId:
                'company.masterdashboard.chart.stickiness_dau_mau',
            dashboardItem: {
                title: 'Monthly average stickiness factor DAU/MAU ',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.stickiness_dau_mau,
                // subtitle: 'Ignored filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'stickiness_dau_mau',
            metricName: 'Stickiness factor',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipNumberValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'company.masterdashboard.activity_30days_monthly'
            ].emitter,
    };
    new_users_daily: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.new_users',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId: 'company.masterdashboard.chart.new_users',
            dashboardItem: {
                title: 'New users',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.new_users_daily,
                // subtitle: 'Ignored filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'new_users',
            metricName: 'New users',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['company.masterdashboard.player_kpis_daily']
                .emitter,
    };
    monthly_avg_dau: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.monthly_avg_dau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId: 'company.masterdashboard.chart.monthly_avg_dau',
            dashboardItem: {
                title: 'Monthly average DAU',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.monthly_avg_dau,
                // subtitle: 'Ignored filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_DAU',
            metricName: 'Monthly avg DAU',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipNumberValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['company.masterdashboard.player_kpis_monthly']
                .emitter,
    };
    mau: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.mau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId: 'company.masterdashboard.chart.mau',
            dashboardItem: {
                title: 'MAU',
                chartDocumentation: CompanyMasterdashboardChartDocs.mau,
                // subtitle: 'Unsupported filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'MAU',
            metricName: 'MAU',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['company.masterdashboard.player_kpis_monthly']
                .emitter,
    };
    new_users_monthly: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.new_users_monthly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId:
                'company.masterdashboard.chart.new_users_monthly',
            dashboardItem: {
                title: 'Monthly new Users ',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.new_users_monthly,
                // subtitle: 'Unsupported filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'new_users',
            metricName: 'Monthly new users',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['company.masterdashboard.player_kpis_monthly']
                .emitter,
    };
    daily_income: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.daily_income',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId: 'company.masterdashboard.chart.daily_income',
            dashboardItem: {
                title: 'Daily income',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.daily_income,
            },
            // yColumnLabelPrefix: 'amount',
            xColumnLabel: 'date',
            metricsGetter: getRevenueMetricsGetter({
                metricId: 'revenue',
                metricName: 'Revenue',
            }),
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['company.masterdashboard.player_kpis_daily']
                .emitter,
    };
    monthly_income: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.monthly_income',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId: 'company.masterdashboard.chart.monthly_income',
            dashboardItem: {
                title: 'Monthly income',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.monthly_income,
            },
            xColumnLabel: 'date',
            metricsGetter: getRevenueMetricsGetter({
                metricId: 'revenue',
                metricName: 'Revenue',
            }),
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['company.masterdashboard.player_kpis_monthly']
                .emitter,
    };
    daily_ads_cost: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.daily_ads_cost',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'company.masterdashboard.chart.daily_ads_cost',
            dashboardName: 'Company Masterdashboard',
            dashboardItem: {
                title: 'Daily ads campaign cost',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.daily_ads_cost,
                // subtitle: 'Ignored filters: Revenue Type, Payer',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) =>
                `cost_${context.settings.currency}`,
            metricName: 'Ads cost',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'company.masterdashboard.campaign_attribution_kpis_daily'
            ].emitter,
    };
    monthly_ads_cost: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.monthly_ads_cost',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            createdInstanceId: 'company.masterdashboard.chart.monthly_ads_cost',
            dashboardName: 'Company Masterdashboard',
            dashboardItem: {
                title: 'Monthly ads campaign cost',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.monthly_ads_cost,
                // subtitle: 'Ignored filters: Revenue Type, Payer',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) =>
                `cost_${context.settings.currency}`,
            metricName: 'Monthly ads cost',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'company.masterdashboard.campaign_attribution_kpis_monthly'
            ].emitter,
    };
    // TODO payers filter
    first_margin_daily: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.first_margin_daily',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId:
                'company.masterdashboard.chart.first_margin_daily',
            dashboardItem: {
                title: 'Daily first margin',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.first_margin_daily,
                // subtitle: 'Ignored filters: Payer',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) =>
                `first_margin_${context.settings.netGross}_${context.settings.currency}`,
            metricName: 'First margin',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'company.masterdashboard.first_margin_daily_last_30days'
            ].emitter,
    };
    first_margin_monthly: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.first_margin_monthly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId:
                'company.masterdashboard.chart.first_margin_monthly',
            dashboardItem: {
                title: 'Monthly first margin',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.first_margin_monthly,
                // subtitle: 'Ignored filters: Payer',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) =>
                `first_margin_${context.settings.netGross}_${context.settings.currency}`,
            metricName: 'Monthly first margin',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'company.masterdashboard.first_margin_monthly'
            ].emitter,
    };
    arpdau: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.daily_arpdau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId: 'company.masterdashboard.chart.daily_arpdau',
            dashboardItem: {
                title: 'Daily ARPDAU',
                chartDocumentation: CompanyMasterdashboardChartDocs.arpdau,
            },
            xColumnLabel: 'date',
            metricsGetter: getRevenueMetricsGetter({
                metricId: 'ARPDAU',
                metricName: 'ARPDAU',
            }),
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['company.masterdashboard.player_kpis_daily']
                .emitter,
    };
    monthly_avg_arpdau: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.monthly_avg_arpdau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId:
                'company.masterdashboard.chart.monthly_avg_arpdau',
            dashboardItem: {
                title: 'Monthly average ARPDAU',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.monthly_avg_arpdau,
            },
            xColumnLabel: 'date',
            metricsGetter: getRevenueMetricsGetter({
                metricId: 'ARPDAU',
                metricName: 'Monthly avg ARPDAU',
            }),
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['company.masterdashboard.player_kpis_monthly']
                .emitter,
    };
    running_arpu: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.running_arpu',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId: 'company.masterdashboard.chart.running_arpu',
            dashboardItem: {
                title: 'Running ARPU',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.running_arpu,
            },
            xColumnLabel: 'day_no',
            metricsGetter: getRevenueMetricsGetter({
                metricId: 'ARPU',
                metricName: 'Running ARPU',
            }),
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) => `Day no: ${label}`,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'company.masterdashboard.cohort_revenue_daily_agg'
            ].emitter,
    };
    monthly_arpu_d30: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.monthly_arpu_d30',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId: 'company.masterdashboard.chart.monthly_arpu_d30',
            dashboardItem: {
                title: 'Monthly ARPU D30',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.monthly_arpu_d30,
            },
            xColumnLabel: 'date',
            metricsGetter: getRevenueMetricsGetter({
                metricId: 'ARPU_d30',
                metricName: 'ARPU d30',
            }),
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['company.masterdashboard.player_kpis_monthly']
                .emitter,
    };
    conversion_rate: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.conversion_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId: 'company.masterdashboard.chart.conversion_rate',
            dashboardItem: {
                title: 'Conversion rate',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.conversion_rate,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'conversion_rate',
            metricName: 'Conversion rate',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipPercentValueFormatter(
                                v
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['company.masterdashboard.player_kpis_daily']
                .emitter,
    };
    monthly_conversion_rate: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.monthly_conversion_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId:
                'company.masterdashboard.chart.monthly_conversion_rate',
            dashboardItem: {
                title: 'Monthly Conversion rate',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.monthly_conversion_rate,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'conversion_rate',
            metricName: 'Monthly conversion rate',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['company.masterdashboard.player_kpis_monthly']
                .emitter,
    };
    daily_cpi: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.daily_cpi',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId: 'company.masterdashboard.chart.daily_cpi',
            dashboardItem: {
                title: 'Daily CPI',
                chartDocumentation: CompanyMasterdashboardChartDocs.daily_cpi,
                // subtitle:
                //     'Total campaign costs over total (Organic & Non-Organic) installs',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                const netGross =
                    context.settings.netGross == 'net' ? 'net_' : 'gross_';
                return `CPI_${context.settings.currency}`;
            },
            metricName: 'CPI',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'company.masterdashboard.campaign_attribution_kpis_daily'
            ].emitter,
    };
    monthly_cpi: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.monthly_cpi',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId: 'company.masterdashboard.chart.monthly_cpi',
            dashboardItem: {
                title: 'Monthly CPI',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.monthly_cpi,
                // subtitle:
                //     'Total campaign costs over total (Organic & Non-Organic) installs',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                const netGross =
                    context.settings.netGross == 'net' ? 'net_' : 'gross_';
                return `CPI_${context.settings.currency}`;
            },
            metricName: 'Monthly CPI',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipCurrencyValueFormatter(
                                v,
                                context.settings.currency || 'pln'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'company.masterdashboard.campaign_attribution_kpis_monthly'
            ].emitter,
    };
    roas_d7_daily: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.roas_d7_daily',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId: 'company.masterdashboard.chart.roas_d7_daily',
            dashboardItem: {
                title: 'Average ROAS D7',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.roas_d7_daily,
                // subtitle:
                //     'Total (Organic & Non-Organic) revenue over total campaign costs',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                const netGross =
                    context.settings.netGross == 'net' ? 'net_' : 'gross_';
                return `ROAS_d7_${netGross}${context.settings.currency}`;
            },
            metricName: 'ROAS D7',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `First open date: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['company.masterdashboard.roas_daily_d7']
                .emitter,
    };
    roas_d7_monthly: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.roas_d7_monthly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId: 'company.masterdashboard.chart.roas_d7_monthly',
            dashboardItem: {
                title: 'Average ROAS D7 monthly',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.roas_d7_monthly,
                // subtitle:
                //     'Total (Organic & Non-Organic) revenue over total campaign costs',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                const netGross =
                    context.settings.netGross == 'net' ? 'net_' : 'gross_';
                return `ROAS_d7_${netGross}${context.settings.currency}`;
            },
            metricName: 'ROAS D7',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['company.masterdashboard.roas_monthly_d7']
                .emitter,
    };
    roas_d30_daily: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.roas_d30_daily',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId: 'company.masterdashboard.chart.roas_d30_daily',
            dashboardItem: {
                title: 'Average ROAS D30',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.roas_d30_daily,
                // subtitle:
                //     'Total (Organic & Non-Organic) revenue over total campaign costs',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                const netGross =
                    context.settings.netGross == 'net' ? 'net_' : 'gross_';
                return `ROAS_d30_${netGross}${context.settings.currency}`;
            },
            metricName: 'ROAS D30',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `First open date: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['company.masterdashboard.roas_daily_d30']
                .emitter,
    };
    roas_d30_monthly: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.roas_d30_monthly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId: 'company.masterdashboard.chart.roas_d30_monthly',
            dashboardItem: {
                title: 'Average ROAS D30 monthly',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.roas_d30_monthly,
                // subtitle:
                //     'Total (Organic & Non-Organic) revenue over total campaign costs',
            },
            xColumnLabel: 'date',
            yColumnLabel: (context: IQueryContext) => {
                const netGross =
                    context.settings.netGross == 'net' ? 'net_' : 'gross_';
                return `ROAS_d30_${netGross}${context.settings.currency}`;
            },
            metricName: 'ROAS D30',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['company.masterdashboard.roas_monthly_d30']
                .emitter,
    };
    // ads engagement rate
    ads_engagement_rate: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.ads_engagement_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId:
                'company.masterdashboard.chart.ads_engagement_rate',
            dashboardItem: {
                title: 'Ads engagement rate',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.ads_engagement_rate,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'engagement_rate',
            metricName: 'Engagement rate',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            EChartsAxisHelper.axisLabelDateFormatter(label),
                        valueFormatter: (v) =>
                            EChartsTooltipHelper.tooltipPercentValueFormatter(
                                v
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'company.masterdashboard.ads_daily_last_30days'
            ].emitter,
    };
    monthly_engagement_rate: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.monthly_engagement_rate',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId:
                'company.masterdashboard.chart.monthly_engagement_rate',
            dashboardItem: {
                title: 'Monthly avg. of daily engagement rate',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.monthly_engagement_rate,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'engagement_rate',
            metricName: 'Monthly avg. of daily engagement rate',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'company.masterdashboard.ads_monthly_last_16months'
            ].emitter,
    };
    ads_impressions: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.ads_impressions',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId: 'company.masterdashboard.chart.ads_impressions',
            dashboardItem: {
                title: 'Ads impressions',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.ads_impressions,
                // subtitle: 'Ignored filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'impressions',
            metricName: 'Ads impressions',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'company.masterdashboard.ads_daily_last_30days'
            ].emitter,
    };
    ads_impressions_monthly: IDashboardItemDataLoaderLink = {
        id: 'company.masterdashboard.chart.ads_impressions_monthly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: 'Company Masterdashboard',
            createdInstanceId:
                'company.masterdashboard.chart.ads_impressions_monthly',
            dashboardItem: {
                title: 'Ads impressions monthly',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.ads_impressions_monthly,
                // subtitle: 'Unsupported filters: Revenue Type',
            },
            xColumnLabel: 'date',
            yColumnLabel: 'impressions',
            metricName: 'Ads impressions monthly',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            `Month: ${EChartsAxisHelper.axisLabelDateFormatter(
                                label
                            )}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'company.masterdashboard.ads_monthly_last_16months'
            ].emitter,
    };
    // impressions_per_deu
    impressions_per_deu: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.ads.chart.impressions_per_deu',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.ads.chart.impressions_per_deu',
            dashboardItem: {
                title: 'Impressions per DEU',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.impressions_per_deu,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'impressions_per_deu',
            metricName: 'Impressions per DEU',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipNumberValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'company.masterdashboard.ads_daily_last_30days'
            ].emitter,
    }; // impressions_per_deu_monthly
    impressions_per_deu_monthly: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.ads.chart.impressions_per_deu_monthly',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.ads.chart.impressions_per_deu_monthly',
            dashboardItem: {
                title: 'Monthly avg. of daily impressions per DEU',
                chartDocumentation:
                    CompanyMasterdashboardChartDocs.impressions_per_deu_monthly,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'impressions_per_deu',
            metricName: 'Monthly avg. of daily impressions per DEU',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                return defaultColorGetterBuilder(this.colorService, context);
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipNumberValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'company.masterdashboard.ads_monthly_last_16months'
            ].emitter,
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    FILTER ITEMS
    ////
    /////////////////////////////////////////////////////////////////

    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        COMPANY_PROJECT_NAME: {
            id: 'company.masterdashboard.filter.project_name',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'company.masterdashboard.filter.project_name',
                datasourceColumnId: 'COMPANY_PROJECT_NAME',
                excludeMode: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'company.masterdashboard.filter.project_name'
                ].emitter,
        },
        FIREBASE_APP_STORE: {
            id: 'company.masterdashboard.filter.firebase_app_store',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'company.masterdashboard.filter.firebase_app_store',
                datasourceColumnId: 'FIREBASE_APP_STORE',
                excludeMode: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'company.masterdashboard.filter.firebase_app_store'
                ].emitter,
        },
        FIREBASE_COUNTRY_TIER: {
            id: 'company.masterdashboard.filter.firebase_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'company.masterdashboard.filter.firebase_country_tier',
                datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
                excludeMode: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'company.masterdashboard.filter.firebase_country_tier'
                ].emitter,
        },
        FIREBASE_COUNTRY: {
            id: 'company.masterdashboard.filter.firebase_country',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'company.masterdashboard.filter.firebase_country',
                datasourceColumnId: 'FIREBASE_COUNTRY',
                excludeMode: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'company.masterdashboard.filter.firebase_country'
                ].emitter,
        },
        // ATTRIBUTION_TRAFFIC_SOURCE: {
        //     id: 'company.masterdashboard.filter.attribution_traffic_source',
        //     dashboardItemFactory: DashboardItemFactories['filter.category']({
        //         createdInstanceId:
        //             'company.masterdashboard.filter.attribution_traffic_source',
        //         datasourceColumnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
        //         componentClass: 'CategoryFilterDashboardItem',
        //         excludeMode: false,
        //     }),
        //     dataLoaderEmitter:
        //         this.filterDataLoaders[
        //             'company.masterdashboard.filter.attribution_traffic_source'
        //         ].emitter,
        // },
        ETL_PAYER: {
            id: 'company.masterdashboard.filter.etl_payer',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId: 'company.masterdashboard.filter.etl_payer',
                datasourceColumnId: 'ETL_PAYER',
                excludeMode: false,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'company.masterdashboard.filter.etl_payer'
                ].emitter,
        },
        // REVENUE_TYPE: {
        //     id: 'company.masterdashboard.filter.revenue_type',
        //     dashboardItemFactory: DashboardItemFactories['filter.category']({
        //         createdInstanceId:
        //             'company.masterdashboard.filter.revenue_type',
        //         datasourceColumnId: 'REVENUE_TYPE',
        //         componentClass: 'CategoryFilterDashboardItem',
        //         excludeMode: false,
        //     }),
        //     dataLoaderEmitter:
        //         this.filterDataLoaders[
        //             'company.masterdashboard.filter.revenue_type'
        //         ].emitter,
        // },
    };

    override ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'CompanyMasterdashboardComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }

    ngAfterViewInit() {
        console.log({
            msg: 'CompanyMasterdashboardComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const projectNameIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'COMPANY_PROJECT_NAME',
                state: {
                    value: { options: [], filterType: 'include' },
                    // filterType: 'include',
                    isFilterApplied: false,
                    isSplitApplied: true,
                },
            };
        const firebaseAppStoreIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_APP_STORE',
                state: {
                    value: {
                        options: [
                            'Amazon Appstore',
                            'App Store',
                            'Google Play',
                            'Mac App Store',
                            'Microsoft Store',
                        ],
                        filterType: 'include',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const firebaseCountryTierIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_COUNTRY_TIER',
                state: {
                    value: {
                        options: ['Tier 1', 'Tier 2', 'Tier 3'],
                        filterType: 'include',
                    },
                    // filterType: 'include',
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [
            projectNameIC,
            // firebaseAppStoreIC,
            // firebaseCountryTierIC,
        ];

        return initialContext;
    }
}
